import { useEffect, useState } from "react";
import Lenis from "lenis";
import { motion, AnimatePresence } from "framer-motion";
import "@/App.css";
import Seo from "@/components/Seo";
import { setLenis } from "@/lib/lenis";
import { SiteMenu } from "@/components/Menu";
import { Lightbox } from "@/components/Lightbox";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { WorkGrid } from "@/components/WorkGrid";
import { Services } from "@/components/Services";
import { About } from "@/components/About";
import { Contact } from "@/components/Contact";

const Intro = ({ onDone }) => (
  <motion.div
    data-testid="intro-overlay"
    initial={{ y: 0 }}
    animate={{ y: "-100%" }}
    transition={{ delay: 1.5, duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
    onAnimationComplete={onDone}
    className="fixed inset-0 z-[100] bg-black flex flex-col items-center justify-center gap-4"
  >
    <div className="overflow-hidden">
      <motion.p
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="font-syncopate font-bold uppercase tracking-tight text-3xl sm:text-5xl"
      >
        KHUSH PATEL
      </motion.p>
    </div>
    <motion.p
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.4, duration: 0.6 }}
      className="font-jetmono text-[10px] tracking-[0.4em] uppercase text-[#808080]"
    >
      PHOTOGRAPHY — FILM — POST
    </motion.p>
  </motion.div>
);

function App() {
  const [introDone, setIntroDone] = useState(false);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    setLenis(lenis);
    let rafId;
    const loop = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      setLenis(null);
    };
  }, []);

  useEffect(() => {
    const blockContextMenu = (e) => e.preventDefault();
    document.addEventListener("contextmenu", blockContextMenu);
    return () => document.removeEventListener("contextmenu", blockContextMenu);
  }, []);

  return (
    <div className="bg-black text-white min-h-screen">
      <Seo
        title="Khush Patel | Photographer & Filmmaker in Los Angeles — Concerts, Fashion, Music Videos"
        siteName="Khush Patel Photography"
        description="Khush Patel is a Los Angeles photographer and filmmaker trusted by 75+ artists — concert and festival photography, published fashion editorials, music video cinematography, DaVinci Resolve color grading and Photoshop retouching. Based in LA, available worldwide."
        image="/media/hero-poster.jpg"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Person",
              "@id": `${typeof window !== "undefined" ? window.location.origin : ""}/#khush-patel`,
              name: "Khush Patel",
              jobTitle: "Photographer & Filmmaker",
              description: "Los Angeles-based photographer and filmmaker — concert and festival photography, fashion editorials, music video cinematography, color grading and retouching.",
              email: "mailto:khushpatel075@gmail.com",
              worksFor: { "@type": "Organization", name: "Khush Patel Photography" },
              address: { "@type": "PostalAddress", addressLocality: "Los Angeles", addressRegion: "CA", addressCountry: "US" },
              nationality: "Indian",
              knowsAbout: [
                "Concert Photography", "Festival Photography", "Fashion Photography", "Portrait Photography",
                "Music Video Production", "Cinematography", "Color Grading", "DaVinci Resolve", "Photo Retouching", "Adobe Photoshop",
              ],
              sameAs: [
                "https://instagram.com/khushpatelphotography",
                "https://voyagela.com/interview/daily-inspiration-meet-khush-patel/",
                "https://canvasrebel.com/meet-khush-patel/",
                "https://boldjourney.com/meet-khush-patel/",
                "https://shoutoutla.com/meet-khush-patel/",
              ],
            },
            {
              "@type": "WebSite",
              name: "Khush Patel Photography",
              inLanguage: "en",
              publisher: { "@id": `${typeof window !== "undefined" ? window.location.origin : ""}/#khush-patel` },
            },
            {
              "@type": "ProfessionalService",
              name: "Khush Patel Photography",
              priceRange: "$$",
              areaServed: ["Los Angeles", "California", "Worldwide"],
              founder: { "@id": `${typeof window !== "undefined" ? window.location.origin : ""}/#khush-patel` },
              email: "mailto:khushpatel075@gmail.com",
              sameAs: ["https://instagram.com/khushpatelphotography"],
              address: { "@type": "PostalAddress", addressLocality: "Los Angeles", addressRegion: "CA", addressCountry: "US" },
            },
          ],
        }}
      />
      <AnimatePresence>{!introDone && <Intro onDone={() => setIntroDone(true)} />}</AnimatePresence>
      <SiteMenu />
      <Lightbox />
      <main>
        <Hero />
        <Marquee />
        <WorkGrid />
        <Services />
        <About />
        <Contact />
      </main>
    </div>
  );
}

export default App;
